import React, { useEffect, useMemo, useState } from "react";
import API_BASE_URL from "../../config";

const CustomFieldsSection = ({ recordType, recordId = null, onValuesChange = null }) => {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const normalizedRecordId = recordId ? Number(recordId) : null;
  const isDraftMode = !normalizedRecordId;

  const getToken = () => localStorage.getItem("token");

  useEffect(() => {
    if (!recordType) {
      setFields([]);
      setValues({});
      setLoading(false);
      return;
    }

    loadCustomFields();
  }, [recordType, normalizedRecordId]);

  const loadCustomFields = async () => {
    try {
      setLoading(true);

      const endpoint = normalizedRecordId
        ? `${API_BASE_URL}/api/custom-fields/values/${recordType}/${normalizedRecordId}`
        : `${API_BASE_URL}/api/custom-fields/definitions/${recordType}`;

      const res = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to load custom fields");
      }

      const fieldList = Array.isArray(data) ? data : [];
      const initialValues = {};

      fieldList.forEach((field) => {
        if (field.field_type === "date") {
          initialValues[field.id] = field.value?.value_date || "";
        } else if (field.field_type === "dropdown") {
          initialValues[field.id] = field.value?.selected_option_id || "";
        } else {
          initialValues[field.id] = field.value?.value_text || "";
        }
      });

      setFields(fieldList);
      setValues(initialValues);
    } catch (err) {
      console.error("Load custom field values error:", err);
      setFields([]);
      setValues({});
    } finally {
      setLoading(false);
    }
  };

  const payloadValues = useMemo(() => {
    return fields.map((field) => {
      const value = values[field.id] ?? "";

      if (field.field_type === "date") {
        return {
          custom_field_definition_id: field.id,
          value_date: value || null
        };
      }

      if (field.field_type === "dropdown") {
        return {
          custom_field_definition_id: field.id,
          selected_option_id: value || null
        };
      }

      return {
        custom_field_definition_id: field.id,
        value_text: value
      };
    });
  }, [fields, values]);

  useEffect(() => {
    if (typeof onValuesChange === "function") {
      onValuesChange(payloadValues);
    }
  }, [payloadValues, onValuesChange]);

  const handleValueChange = (fieldId, value) => {
    setValues((prev) => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSave = async () => {
    if (!normalizedRecordId) return;

    try {
      setSaving(true);

      const res = await fetch(
        `${API_BASE_URL}/api/custom-fields/values/${recordType}/${normalizedRecordId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            values: payloadValues
          })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to save custom fields");
      }

      alert("Custom fields saved.");
      loadCustomFields();
    } catch (err) {
      console.error("Save custom field values error:", err);
      alert(err.message || "Failed to save custom fields.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!fields.length) {
    return null;
  }

  return (
    <>
      <div className="sales-field sales-field-full custom-fields-divider">
        <h3>Custom Fields</h3>
      </div>

      {fields.map((field) => (
        <div key={field.id} className="sales-field">
          <label>{field.label}</label>

          {field.field_type === "date" ? (
            <input
              type="date"
              value={values[field.id] || ""}
              onChange={(e) => handleValueChange(field.id, e.target.value)}
            />
          ) : field.field_type === "dropdown" ? (
            <select
              value={values[field.id] || ""}
              onChange={(e) => handleValueChange(field.id, e.target.value)}
            >
              <option value="">Select</option>
              {(field.options || [])
                .filter((option) => option.is_active !== 0)
                .map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.option_label}
                  </option>
                ))}
            </select>
          ) : (
            <input
              type="text"
              value={values[field.id] || ""}
              onChange={(e) => handleValueChange(field.id, e.target.value)}
            />
          )}
        </div>
      ))}

      {!isDraftMode && (
        <div className="sales-field sales-field-full">
          <button
            type="button"
            className="sales-secondary-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving Custom Fields..." : "Save Custom Fields"}
          </button>
        </div>
      )}
    </>
  );
};

export default CustomFieldsSection;